import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { smileCaseType } from "../../types/SmilePortal/smileCaseTypes";
import { isEqual } from "lodash";
import { RootState } from "../store";
import api from "../../utils/api";

interface PaginationState {
    totalCount: number;
    totalPages: number;
    currentPage: number;
}

interface SyncSmileImagesPayload {
    smileId: string;
    imageData: {
        box_id: string;
        name: string;
        type: string;
    }[];
}

interface FullCaseData {
    caseDetails: {
        smile_id: string;
        patient_name: string;
        box_folder_id?:number;
        case_id?: string | null;
    };
    caseFiles: any[];
}

interface CasesState {
    allSmileCases: smileCaseType[];
    userSmileCases: smileCaseType[];

    selectedCase: FullCaseData | null;

    allCasesPagination: PaginationState;
    userCasesPagination: PaginationState;

    isLoading: boolean;
    isCreating: boolean;
    isSyncingImages: boolean;
    isFetchingCase: boolean;
    fetchingSmileId: string | null;

    error: string | null;
}

const initialState: CasesState = {
    allSmileCases: [],
    userSmileCases: [],

    selectedCase: null,

    allCasesPagination: {
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
    },

    userCasesPagination: {
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
    },

    isLoading: false,
    isCreating: false,
    isSyncingImages: false,
    isFetchingCase: false,
    fetchingSmileId: null,

    error: null,
};

interface GetCasesParams {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
}

interface GetUserCasesParams extends GetCasesParams {
    user_id: number;
}

interface CreateSmileCasePayload {
    user_id: number;
    patient_name: string;
    /** Lab token of the client this smile case belongs to */
    upload_token?: string;
    additional_info?: string;
}

export const createSmileCase = createAsyncThunk(
    "smileDesign/createSmileCase",
    async (
        payload: CreateSmileCasePayload,
        { rejectWithValue }
    ) => {
        try {
            const response = await api.post(
                "/smile-design/cases/create",
                payload
            );

            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                "Failed to create smile case"
            );
        }
    }
);

export const syncSmileImages = createAsyncThunk(
    "smileDesign/syncSmileImages",
    async (
        payload: SyncSmileImagesPayload,
        { rejectWithValue }
    ) => {
        try {
            const response = await api.post(
                "/smile-design/cases/files/entry",
                payload
            );

            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                "Failed to sync smile images"
            );
        }
    }
);


export const getAllSmileCases = createAsyncThunk(
    "smileDesign/getAllSmileCases",
    async (params: GetCasesParams, { rejectWithValue, getState }) => {
        try {
            const { page = 1, limit = 20, search = "", status = "" } = params;
            const response = await api.get(`/smile-design/cases/all`, {
                params: { page, limit, search, status },
            });

            const newData = response.data.data;
            const currentSmileCases = (getState() as RootState).smileCases.allSmileCases;

            const isSame = isEqual(currentSmileCases, newData?.data || []);

            return {
                ...newData,
                __skipUpdate: isSame,
            };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch cases"
            );
        }
    }
);


export const getUserAllSmileCases = createAsyncThunk(
    "smileDesign/getUserAllSmileCases",
    async (
        { user_id, page = 1, limit = 20 }: GetUserCasesParams,
        { rejectWithValue, getState }
    ) => {
        try {
            const response = await api.get(
                `/smile-design/cases/${user_id}`,
                {
                    params: { page, limit },
                }
            );

            const newData = response.data.data;

            const currentSmileCases = (
                getState() as RootState
            ).smileCases.userSmileCases;

            const isSame = isEqual(
                currentSmileCases,
                newData?.data || []
            );

            return {
                ...newData,
                __skipUpdate: isSame,
            };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                "Failed to fetch user smile cases"
            );
        }
    }
);


export const fetchFullCaseBySmileId = createAsyncThunk(
    "smileDesign/fetchFullCaseBySmileId",
    async (
        smileId: string,
        { rejectWithValue }
    ) => {
        try {
            const response = await api.get(
                `/smile-design/cases/casedetail/${smileId}`
            );

            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                "Failed to fetch case details"
            );
        }
    }
);

const caseSlice = createSlice({
    name: "smileCases",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder

            // create a new smile case
            .addCase(createSmileCase.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })

            .addCase(createSmileCase.fulfilled, (state, action) => {
                state.isCreating = false;

                // prepend newly created case
                state.userSmileCases.unshift(action.payload);

                state.userCasesPagination.totalCount += 1;
            })

            .addCase(createSmileCase.rejected, (state, action) => {
                state.isCreating = false;
                state.error = action.payload as string;
            })


            // sync smile images slice
            .addCase(syncSmileImages.pending, (state) => {
                state.isSyncingImages = true;
                state.error = null;
            })

            .addCase(syncSmileImages.fulfilled, (state) => {
                state.isSyncingImages = false;
            })

            .addCase(syncSmileImages.rejected, (state, action) => {
                state.isSyncingImages = false;
                state.error = action.payload as string;
            })

            // Get all smile cases
            .addCase(getAllSmileCases.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allCasesPagination = {
                    totalCount: action.payload.totalCount || 0,
                    totalPages: action.payload.totalPages || 0,
                    currentPage: action.payload.currentPage || 1,
                };

                if (!action.payload.__skipUpdate) {
                    state.allSmileCases = action.payload.data || [];
                }
            })

            // user all smile cases
            .addCase(getUserAllSmileCases.fulfilled, (state, action) => {
                state.isLoading = false;

                state.userCasesPagination = {
                    totalCount: action.payload.totalCount || 0,
                    totalPages: action.payload.totalPages || 0,
                    currentPage: action.payload.page || 1,
                };

                if (!action.payload.__skipUpdate) {
                    state.userSmileCases = action.payload.data || [];
                }
            })


            // fetch full case details
            .addCase(fetchFullCaseBySmileId.pending, (state, action) => {
                state.isFetchingCase = true;
                state.selectedCase = null;
                state.fetchingSmileId = action.meta.arg;
                state.error = null;
            })

            .addCase(fetchFullCaseBySmileId.fulfilled, (state, action) => {
                if (action.meta.arg !== state.fetchingSmileId) return;
                state.isFetchingCase = false;
                state.selectedCase = action.payload;
            })

            .addCase(fetchFullCaseBySmileId.rejected, (state, action) => {
                if (action.meta.arg !== state.fetchingSmileId) return;
                state.isFetchingCase = false;
                state.error = action.payload as string;
            });
    },
});

export default caseSlice.reducer;
